'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import Script from 'next/script'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ErrorMessage } from '@/components/ui/fieldset'
import { recaptchaSiteKey } from '@/config/env.public'

type ReCaptchaProps = {
  action: string
  /** Page locale, so Google renders "Ik ben geen robot" rather than "I'm not a robot" on /nl. */
  locale: string
  onChange: (token: string | null) => void
  error?: string | null
  resetKey?: number
  className?: string
  unavailableLabel: string
}

export function ReCaptcha({
  action,
  locale,
  onChange,
  error = null,
  resetKey = 0,
  className = '',
  unavailableLabel,
}: ReCaptchaProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const widgetRef = useRef<number | null>(null)
  const callbackRef = useRef(onChange)
  const renderedElementRef = useRef<HTMLDivElement | null>(null)
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
  /**
   * reCAPTCHA is around 870 KB and the forms that use it sit below the fold, so loading it with
   * the page spends that on every visitor who never reaches a form. It waits until the widget is
   * near the viewport — long before anyone has finished typing, and free for everyone else.
   */
  const [inView, setInView] = useState(false)
  const [internalError, setInternalError] = useState<string | null>(null)
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') {
      return 'light'
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })
  const renderKey = `${theme}-${action}-${locale}`
  const unavailableError = recaptchaSiteKey ? null : unavailableLabel

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerEl(node)
  }, [])

  useEffect(() => {
    callbackRef.current = onChange
  }, [onChange])

  useEffect(() => {
    const node = wrapperRef.current
    if (!node || inView) {
      return
    }
    if (typeof IntersectionObserver === 'undefined') {
      // No observer to wait on, so load it — but not synchronously inside the effect, which would
      // render twice for nothing. The initial state has to match the server's either way.
      queueMicrotask(() => setInView(true))
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: '400px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [inView])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (event: MediaQueryListEvent) => setTheme(event.matches ? 'dark' : 'light')
    media.addEventListener('change', handler)
    return () => media.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    widgetRef.current = null
    renderedElementRef.current = null
  }, [renderKey])

  useEffect(() => {
    if (!recaptchaSiteKey || !containerEl) {
      return
    }

    let cancelled = false
    let retryTimeout: ReturnType<typeof setTimeout> | null = null
    let attempts = 0

    const attemptRender = () => {
      if (cancelled) {
        return
      }

      if (!containerEl) {
        retryTimeout = setTimeout(attemptRender, 150)
        return
      }

      if (renderedElementRef.current === containerEl) {
        return
      }

      const grecaptcha = window.grecaptcha?.enterprise
      if (!grecaptcha) {
        attempts += 1
        if (attempts > 20) {
          setTimeout(() => setInternalError(unavailableLabel), 0)
          return
        }
        retryTimeout = setTimeout(attemptRender, 250)
        return
      }

      grecaptcha.ready(() => {
        if (cancelled || !containerEl) {
          return
        }

        if (renderedElementRef.current === containerEl) {
          return
        }

        wrapperRef.current?.setAttribute('data-loaded', 'false')
        renderedElementRef.current = containerEl
        widgetRef.current = grecaptcha.render(containerEl, {
          sitekey: recaptchaSiteKey,
          action,
          callback: (token: string) => callbackRef.current(token),
          'expired-callback': () => callbackRef.current(null),
          'error-callback': () => {
            callbackRef.current(null)
            setInternalError(unavailableLabel)
          },
          theme,
        })
        wrapperRef.current?.setAttribute('data-loaded', 'true')
      })
    }

    attemptRender()

    return () => {
      cancelled = true
      if (retryTimeout) {
        clearTimeout(retryTimeout)
      }
    }
  }, [action, theme, unavailableLabel, containerEl])

  useEffect(() => {
    if (!resetKey || widgetRef.current === null || !window.grecaptcha?.enterprise) {
      return
    }

    try {
      window.grecaptcha.enterprise.reset(widgetRef.current)
    } catch {
      // Google's script throws on a widget id it no longer knows (e.g. the script instance was
      // replaced under us). A stale widget must not take down the page; clearing the token below
      // already forces a fresh solve either way.
    }
    callbackRef.current(null)
  }, [resetKey])

  const helperText = error || internalError || unavailableError

  return (
    <div>
      {recaptchaSiteKey && inView && (
        <Script
          id="recaptcha"
          src={`https://www.google.com/recaptcha/enterprise.js?render=explicit&hl=${locale}`}
          strategy="afterInteractive"
          async
          defer
        />
      )}
      <div ref={wrapperRef} data-loaded="false" className="relative h-[76px] w-[302px] rounded-[3px]">
        <div aria-hidden="true" className="absolute inset-0 rounded-[3px] bg-white/80 dark:bg-black/40" />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity data-[loaded=true]:opacity-0">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-mist-950/20 border-t-mist-950/60 dark:border-white/20 dark:border-t-white/70" />
        </div>
        <div key={renderKey} ref={setContainerRef} className={`relative z-10 ${className}`} />
      </div>
      {helperText && <ErrorMessage className="mt-2">{helperText}</ErrorMessage>}
    </div>
  )
}
